/**
 * Atalhos de mensagem do Chat: o catálogo de variáveis e as regras que a tela de cadastro, o popover do
 * "/" e a API compartilham (docs/propostas/chat/menu-e-atalhos.html §8 e §9).
 *
 * O catálogo e a normalização do comando existem também na API (AtalhoService); mudou aqui, muda lá.
 */

export interface VariavelAtalho {
    chave: string;
    /** O que a variável vira, em uma frase — aparece na dica do cadastro. */
    descricao: string;
}

export const VARIAVEIS_ATALHO: VariavelAtalho[] = [
    { chave: 'nome', descricao: 'Nome completo do contato' },
    { chave: 'primeiro_nome', descricao: 'Só o primeiro nome do contato' },
    { chave: 'vendedor', descricao: 'Quem está atendendo (você)' },
    { chave: 'empresa', descricao: 'O nome da sua empresa' },
    { chave: 'valor', descricao: 'Valor da oportunidade' },
    { chave: 'etapa', descricao: 'Etapa da oportunidade' },
];

const CHAVES = new Set(VARIAVEIS_ATALHO.map(v => v.chave));

const semAcento = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '');

/**
 * Minúsculo, sem acento, sem espaço (vira traço) e sem a barra inicial; só letras, números, "_" e "-".
 * É o que a API faz ao gravar — a tela aplica enquanto a pessoa digita, para não recusar depois.
 */
export const normalizarComando = (texto: string): string =>
    semAcento(texto.trim().toLowerCase())
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9_-]/g, '')
        .replace(/^-+/, '');

/** Busca sem acento e sem caixa, para o "/orc" achar "Orçamento". */
export const paraBusca = (texto: string): string => semAcento(texto.toLowerCase());

const PADRAO_VARIAVEL = /\{\{\s*([^{}]*?)\s*\}\}/g;

export interface VariavelInvalida {
    /** Como foi escrita, dentro das chaves. */
    nome: string;
    /** A variável do catálogo mais parecida, quando há uma. */
    sugestao: string | null;
}

const distancia = (a: string, b: string): number => {
    const linha = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        let anterior = linha[0];
        linha[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const guardado = linha[j];
            linha[j] = Math.min(linha[j] + 1, linha[j - 1] + 1, anterior + (a[i - 1] === b[j - 1] ? 0 : 1));
            anterior = guardado;
        }
    }
    return linha[b.length];
};

const sugerir = (nome: string): string | null => {
    const alvo = paraBusca(nome).replace(/[^a-z0-9]/g, '');
    if (!alvo) return null;
    let melhor: string | null = null;
    let menor = Infinity;
    for (const chave of CHAVES) {
        const d = distancia(alvo, chave.replace(/_/g, ''));
        if (d < menor) { menor = d; melhor = chave; }
    }
    /* Mais que três letras de diferença deixa de ser erro de digitação e vira outra palavra. */
    return melhor && menor <= 3 ? melhor : null;
};

/** As variáveis do texto que não existem no catálogo, cada uma com a sugestão mais próxima. */
export const variaveisInvalidas = (corpo: string): VariavelInvalida[] => {
    const vistas = new Set<string>();
    const saida: VariavelInvalida[] = [];
    for (const m of corpo.matchAll(PADRAO_VARIAVEL)) {
        const nome = m[1];
        if (CHAVES.has(nome) || vistas.has(nome)) continue;
        vistas.add(nome);
        saida.push({ nome, sugestao: sugerir(nome) });
    }
    return saida;
};

/** Nesta parte do texto, o trecho `{{...}}` que não existe no catálogo (para o espelho em vermelho). */
export interface PedacoTexto {
    texto: string;
    tipo: 'texto' | 'variavel' | 'invalida';
}

export const partirPorVariaveis = (corpo: string): PedacoTexto[] => {
    const saida: PedacoTexto[] = [];
    let ultimo = 0;
    for (const m of corpo.matchAll(PADRAO_VARIAVEL)) {
        const inicio = m.index ?? 0;
        if (inicio > ultimo) saida.push({ texto: corpo.slice(ultimo, inicio), tipo: 'texto' });
        saida.push({ texto: m[0], tipo: CHAVES.has(m[1]) ? 'variavel' : 'invalida' });
        ultimo = inicio + m[0].length;
    }
    if (ultimo < corpo.length) saida.push({ texto: corpo.slice(ultimo), tipo: 'texto' });
    return saida;
};

/** O que a tela do Chat sabe da conversa aberta, no momento de inserir. */
export interface ContextoAtalho {
    nome?: string | null;
    vendedor?: string | null;
    empresa?: string | null;
    valor?: number | null;
    etapa?: string | null;
}

export interface AtalhoResolvido {
    texto: string;
    /** Variáveis que ficaram sem valor — continuam escritas no texto, destacadas pela tela. */
    semValor: string[];
}

const formatarValor = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Troca `{{variavel}}` pelo valor que a tela já tem. **Variável sem valor não some nem trava o envio**:
 * fica escrita como `{{valor}}` e vai na lista `semValor`, para a tela destacar — um buraco silencioso
 * ("Oi , tudo bem?") sairia para o cliente sem ninguém ver.
 */
export const resolverAtalho = (corpo: string, ctx: ContextoAtalho): AtalhoResolvido => {
    const nome = (ctx.nome ?? '').trim();
    const valores: Record<string, string> = {
        nome,
        primeiro_nome: nome.split(/\s+/)[0] ?? '',
        vendedor: (ctx.vendedor ?? '').trim(),
        empresa: (ctx.empresa ?? '').trim(),
        /* Valor zero é "ainda não há proposta", não "zero reais": não vai para o cliente. */
        valor: ctx.valor ? formatarValor(ctx.valor) : '',
        etapa: (ctx.etapa ?? '').trim() === '—' ? '' : (ctx.etapa ?? '').trim(),
    };

    const semValor: string[] = [];
    const texto = corpo.replace(PADRAO_VARIAVEL, (inteiro, chave: string) => {
        if (!CHAVES.has(chave)) return inteiro;
        const valor = valores[chave];
        if (valor) return valor;
        if (!semValor.includes(chave)) semValor.push(chave);
        return `{{${chave}}}`;
    });
    return { texto, semValor };
};
